import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';
import {
  APP_ACTION,
  APP_BORDERS,
  APP_BRAND,
  APP_CSS_VARS,
  APP_SEMANTIC,
  APP_SHAPE,
  APP_SURFACES,
  APP_TEXT_COLOR,
  type AppColorMode,
} from '../constants/colors';
import { APP_FONT_FAMILY } from '../constants/fonts';
import { appTypography, appTypographyComponents } from '../constants/typography';

export function createAppTheme(mode: AppColorMode, direction: 'ltr' | 'rtl' = 'ltr') {
  const surfaces = APP_SURFACES[mode];
  const borders = APP_BORDERS[mode];
  const action = APP_ACTION[mode];
  const isDark = mode === 'dark';

  const palette = {
    mode,
    primary: { ...APP_BRAND.primary },
    secondary: { ...APP_BRAND.secondary },
    success: { ...APP_SEMANTIC.success },
    error: { ...APP_SEMANTIC.error },
    warning: { ...APP_SEMANTIC.warning },
    info: { ...APP_SEMANTIC.info },
    background: {
      default: surfaces.default,
      paper: surfaces.paper,
      elevated: surfaces.elevated,
      input: surfaces.input,
      inputFocused: surfaces.inputFocused,
      shell: surfaces.shell,
    },
    text: {
      primary: APP_TEXT_COLOR[mode].primary,
      secondary: APP_TEXT_COLOR[mode].secondary,
      disabled: APP_TEXT_COLOR[mode].disabled,
    },
    divider: borders.default,
    action: {
      hover: action.hover,
      selected: action.selected,
      focus: action.focusRing,
    },
  } as const;

  const cardShadow = isDark
    ? '0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.35)'
    : '0 1px 3px rgba(26, 35, 50, 0.07), 0 1px 2px rgba(26, 35, 50, 0.04)';

  const cardHoverShadow = isDark
    ? '0 8px 28px rgba(0, 0, 0, 0.55)'
    : '0 8px 24px rgba(26, 35, 50, 0.10)';

  const themeOptions: ThemeOptions = {
    direction,
    typography: appTypography,
    shape: {
      borderRadius: APP_SHAPE.radiusMd,
    },
    palette,
    components: {
      ...appTypographyComponents,
      MuiCssBaseline: {
        styleOverrides: {
          ...(appTypographyComponents?.MuiCssBaseline?.styleOverrides as object),
          ':root': {
            '--esg-primary': APP_CSS_VARS.primary,
            '--esg-primary-rgb': APP_CSS_VARS.primaryRgb,
            '--esg-bg-default': surfaces.default,
            '--esg-bg-paper': surfaces.paper,
            '--esg-text-primary': APP_TEXT_COLOR[mode].primary,
            '--esg-text-secondary': APP_TEXT_COLOR[mode].secondary,
            '--esg-border': borders.default,
            '--app-font-family': APP_FONT_FAMILY,
          },
          body: {
            fontFamily: APP_FONT_FAMILY,
            backgroundColor: surfaces.default,
            color: APP_TEXT_COLOR[mode].primary,
          },
          '::-webkit-scrollbar-thumb': {
            background: alpha(APP_BRAND.primary.main, isDark ? 0.32 : 0.22),
            borderRadius: 4,
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: alpha(APP_BRAND.primary.main, isDark ? 0.46 : 0.34),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: APP_SHAPE.radiusMd,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          },
          contained: {
            boxShadow: 'none',
            backgroundColor: APP_BRAND.primary.main,
            '&:hover': {
              backgroundColor: APP_BRAND.primary.dark,
              boxShadow: `0 4px 16px ${alpha(APP_BRAND.primary.main, 0.35)}`,
            },
          },
          outlined: {
            borderColor: borders.strong,
            '&:hover': {
              borderColor: APP_BRAND.primary.main,
              backgroundColor: alpha(APP_BRAND.primary.main, isDark ? 0.08 : 0.06),
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: APP_SHAPE.radiusLg,
            backgroundColor: surfaces.paper,
            border: `1px solid ${borders.subtle}`,
            boxShadow: cardShadow,
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: cardHoverShadow,
              borderColor: borders.default,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: APP_SHAPE.radiusMd,
            backgroundImage: 'none',
            backgroundColor: surfaces.paper,
          },
          outlined: {
            borderColor: borders.default,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundColor: surfaces.shell,
            color: APP_TEXT_COLOR[mode].primary,
            borderBottom: `1px solid ${borders.default}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: surfaces.shell,
            borderColor: borders.default,
            borderRight: `1px solid ${borders.default}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: APP_SHAPE.radiusSm,
            backgroundColor: surfaces.input,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: borders.strong,
            },
            '&.Mui-focused': {
              backgroundColor: surfaces.inputFocused,
              boxShadow: `0 0 0 3px ${action.focusRing}`,
            },
          },
          notchedOutline: {
            borderColor: borders.default,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
          outlined: {
            borderColor: borders.strong,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: borders.subtle,
            fontSize: '1rem',
          },
          head: {
            backgroundColor: isDark ? alpha(surfaces.elevated, 0.6) : surfaces.elevated,
            color: APP_TEXT_COLOR[mode].secondary,
            fontSize: '0.9375rem',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: borders.default,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: APP_SHAPE.radiusLg,
            border: `1px solid ${borders.default}`,
            boxShadow: isDark
              ? '0 24px 48px rgba(0, 0, 0, 0.55)'
              : '0 24px 48px rgba(26, 35, 50, 0.12)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: alpha(APP_SEMANTIC.success.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.success.light : APP_SEMANTIC.success.dark,
          },
          standardError: {
            backgroundColor: alpha(APP_SEMANTIC.error.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.error.light : APP_SEMANTIC.error.dark,
          },
          standardWarning: {
            backgroundColor: alpha(APP_SEMANTIC.warning.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.warning.light : APP_SEMANTIC.warning.dark,
          },
          standardInfo: {
            backgroundColor: alpha(APP_SEMANTIC.info.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.info.light : APP_SEMANTIC.info.dark,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
}
